<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import { useCart } from '../stores/cartStore'

const { addItem, isInCart, increaseQuantity, decreaseQuantity, items } = useCart()
const detailQuantity = ref(1)
const API_URL = import.meta.env.VITE_API_URL
const user = ref(null)
const products = ref([])
const loading = ref(true)
const selectedProduct = ref(null)
const showPurchaseModal = ref(false)
const showDetailModal = ref(false)
const showSteamRequiredModal = ref(false)
const detailProduct = ref(null)
const purchasing = ref(false)
const purchaseResult = ref(null)
const showSortDropdown = ref(false)
const showFilters = ref(false)

const openLoginModal = () => {
  window.dispatchEvent(new Event('open-login'))
}

const steamLinkUrl = computed(() => {
  const token = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('steam_token') || ''
    : ''
  return `${API_URL}/api/auth/steam/link?token=${encodeURIComponent(token)}`
})

const sortOptions = [
  { value: 'cheapest', label: 'Sort by cheapest' },
  { value: 'expensive', label: 'Sort by expensive' }
]

const currentSortLabel = computed(() => {
  return sortOptions.find(o => o.value === sortBy.value)?.label || 'Sort by'
})

const selectSort = (value) => {
  sortBy.value = value
  showSortDropdown.value = false
}

const toggleSortDropdown = () => {
  showSortDropdown.value = !showSortDropdown.value
}

onMounted(async () => {
  document.querySelector('.bg-image')?.classList.add('market-bg')
  window.addEventListener('resize', handleResize)
  await Promise.all([loadProducts(), loadUser(), loadSettings()])
  await loadCategories()
})
onUnmounted(() => {
  document.querySelector('.bg-image')?.classList.remove('market-bg')
  window.removeEventListener('resize', handleResize)
})

const loadUser = async () => {
  const token = localStorage.getItem('steam_token')
  if (token) {
    try {
      const response = await axios.get(`${API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.data.user) {
        user.value = response.data.user
      }
    } catch (e) {
      console.error('Failed to load user')
    }
  }
}

const loadProducts = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/products`)
    products.value = response.data || []
  } catch (e) {
    products.value = []
  }
  loading.value = false
}

const loadSettings = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/settings`)
    if (response.data) {
      priceRangeMin.value = response.data.priceRangeMin || 0
      priceRangeMax.value = response.data.priceRangeMax || 99999999
      priceMin.value = priceRangeMin.value
      priceMax.value = priceRangeMax.value
    }
  } catch (e) {
    // Use defaults
  }
}

const loadCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/categories`)
    if (response.data && response.data.length > 0) {
      const productCats = new Set(products.value.map(p => p.category).filter(Boolean))
      categories.value = [
        { id: 'all', name: 'All Items', active: true },
        ...response.data
          .filter(c => productCats.has(c.name))
          .map(c => ({ id: c.name, name: c.name, active: false }))
      ]
    } else {
      // Fallback to default categories if none in DB
      categories.value = [
        { id: 'all', name: 'All Items', active: true },
        { id: 'items', name: 'Items', active: false },
        { id: 'dinos', name: 'Dinos', active: false },
        { id: 'kits', name: 'Kits', active: false },
        { id: 'services', name: 'Services', active: false },
      ]
    }
  } catch (e) {
    // Fallback to default categories
    categories.value = [
      { id: 'all', name: 'All Items', active: true },
      { id: 'items', name: 'Items', active: false },
      { id: 'dinos', name: 'Dinos', active: false },
      { id: 'kits', name: 'Kits', active: false },
      { id: 'services', name: 'Services', active: false },
    ]
  }
}

const openDetail = (product) => {
  detailProduct.value = product
  detailQuantity.value = 1
  showDetailModal.value = true
}

const closeDetail = () => {
  showDetailModal.value = false
  detailProduct.value = null
  detailQuantity.value = 1
}

const addToCartWithQuantity = (product) => {
  if (!user.value) {
    openLoginModal()
    return
  }
  if (!user.value.hasSteam) {
    showSteamRequiredModal.value = true
    return
  }
  const productWithQty = { ...product, quantity: detailQuantity.value }
  addItem(productWithQty)
}

const openPurchaseFromDetail = () => {
  if (detailProduct.value) {
    selectedProduct.value = detailProduct.value
    purchaseResult.value = null
    showDetailModal.value = false
    showPurchaseModal.value = true
  }
}

const openPurchase = (product) => {
  selectedProduct.value = product
  purchaseResult.value = null
  showPurchaseModal.value = true
}

const addToCart = (product) => {
  if (!user.value) {
    openLoginModal()
    return
  }
  if (!user.value.hasSteam) {
    showSteamRequiredModal.value = true
    return
  }
  addItem(product)
}

const closePurchase = () => {
  showPurchaseModal.value = false
  selectedProduct.value = null
  purchaseResult.value = null
}

const confirmPurchase = async () => {
  if (user.value && !user.value.hasSteam) {
    showSteamRequiredModal.value = true
    return
  }
  if (!user.value || !selectedProduct.value) return
  
  purchasing.value = true
  purchaseResult.value = null
  
  try {
    const token = localStorage.getItem('steam_token')
    const response = await axios.post(`${API_URL}/api/shop/purchase`, {
      productId: selectedProduct.value.id,
      productName: selectedProduct.value.name,
      productPrice: selectedProduct.value.price,
      quantity: 1
    }, {
      headers: { Authorization: `Bearer ${token}` }
    })
    
    const data = response.data

    if (data.refunded) {
      // Item not deliverable — balance was refunded
      user.value.balance = data.newBalance
      purchaseResult.value = { success: false, message: data.message || 'Item unavailable. Balance refunded.' }
    } else if (data.success) {
      user.value.balance = data.newBalance
      const delivery = data.delivery || {}
      if (delivery.status === 'pending') {
        purchaseResult.value = { success: true, message: `Purchased! ${delivery.message || 'Item will be delivered when you are online.'}` }
      } else {
        purchaseResult.value = { success: true, message: 'Purchase successful! Item delivered.' }
      }
      setTimeout(() => {
        closePurchase()
      }, 2500)
    } else {
      purchaseResult.value = { success: false, message: data.message || 'Purchase failed' }
    }
  } catch (e) {
    purchaseResult.value = { 
      success: false, 
      message: e.response?.data?.message || e.response?.data?.error || 'Purchase failed' 
    }
  } finally {
    purchasing.value = false
  }
}

const canAfford = computed(() => {
  if (!user.value || !selectedProduct.value) return false
  return user.value.balance >= selectedProduct.value.price
})

const categories = ref([
  { id: 'all', name: 'All Items', active: true }
])

const priceMin = ref(0)
const priceMax = ref(99999999)
const priceRangeMin = ref(0)
const priceRangeMax = ref(99999999)
const searchQuery = ref('')
const selectedCategory = ref('all')
const sortBy = ref('cheapest')
const currentPage = ref(1)
const isMobile = ref(window.innerWidth <= 768)
const itemsPerPage = computed(() => isMobile.value ? 10 : 9)

const handleResize = () => { isMobile.value = window.innerWidth <= 768 }

const selectCategory = (id) => {
  categories.value.forEach(c => c.active = c.id === id)
  selectedCategory.value = id
}

const searchTokens = computed(() =>
  searchQuery.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
)

const filteredItems = computed(() => {
  const tokens = searchTokens.value
  let items = products.value.filter(item => {
    const matchesCategory = selectedCategory.value === 'all' || item.category === selectedCategory.value
    const matchesPrice = item.price >= priceMin.value && item.price <= priceMax.value
    let matchesSearch = true
    if (tokens.length) {
      const haystack = `${item.name || ''} ${item.description || ''} ${item.category || ''}`.toLowerCase()
      matchesSearch = tokens.every(t => haystack.includes(t))
    }
    return matchesCategory && matchesPrice && matchesSearch && item.inStock !== false
  })
  
  // Sort items
  switch (sortBy.value) {
    case 'cheapest':
      items = items.sort((a, b) => a.price - b.price)
      break
    case 'expensive':
      items = items.sort((a, b) => b.price - a.price)
      break
    case 'newest':
      items = items.sort((a, b) => b.id - a.id)
      break
    case 'name':
      items = items.sort((a, b) => a.name.localeCompare(b.name))
      break
  }
  
  return items
})

const totalPages = computed(() => Math.ceil(filteredItems.value.length / itemsPerPage.value))

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredItems.value.slice(start, end)
})

const visiblePages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const delta = isMobile.value ? 1 : 2
  const pages = []
  
  const left = Math.max(2, current - delta)
  const right = Math.min(total - 1, current + delta)
  
  pages.push(1)
  if (left > 2) pages.push('...')
  for (let i = left; i <= right; i++) pages.push(i)
  if (right < total - 1) pages.push('...')
  if (total > 1) pages.push(total)
  
  return pages
})

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const resetFilters = () => {
  searchQuery.value = ''
  priceMin.value = priceRangeMin.value
  priceMax.value = priceRangeMax.value
  sortBy.value = 'cheapest'
  selectCategory('all')
  currentPage.value = 1
}

const selectCategoryAndReset = (id) => {
  selectCategory(id)
  currentPage.value = 1
}

watch([searchQuery, priceMin, priceMax], () => {
  currentPage.value = 1
})
</script>

<template>
  <main class="main-content">
    <div class="page-header">
      <h1 class="page-title">MARKET</h1>
      <p class="page-subtitle">Purchase in-game items, resources, and exclusive perks</p>
    </div>

    <div class="market-layout">
      <!-- Mobile Filter Toggle -->
      <button class="mobile-filter-toggle" @click="showFilters = !showFilters">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/></svg>
        {{ showFilters ? 'HIDE FILTERS' : 'FILTERS' }}
      </button>
      <!-- Sidebar Filters -->
      <aside class="market-sidebar" :class="{ 'sidebar-open': showFilters }">
        <div class="filter-section">
          <h3 class="filter-title">Search</h3>
          <div class="search-wrapper">
            <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input 
              type="text" 
              class="search-input" 
              placeholder="Search items..."
              v-model="searchQuery"
            >
          </div>
        </div>

        <div class="filter-section">
          <h3 class="filter-title">Sort By</h3>
          <div class="custom-select" @click="toggleSortDropdown">
            <div class="select-trigger">
              <span>{{ currentSortLabel }}</span>
              <svg class="select-arrow" :class="{ open: showSortDropdown }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
            <div class="select-options" v-show="showSortDropdown">
              <div 
                v-for="option in sortOptions" 
                :key="option.value"
                class="select-option"
                :class="{ active: sortBy === option.value }"
                @click.stop="selectSort(option.value)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
        </div>

        <div class="filter-section">
          <h3 class="filter-title">Price Range</h3>
          <div class="price-range">
            <input type="number" class="price-input" v-model="priceMin" placeholder="0">
            <span class="price-separator">-</span>
            <input type="number" class="price-input" v-model="priceMax" placeholder="1000">
          </div>
        </div>

        <div class="filter-section">
          <h3 class="filter-title">Category</h3>
          <div class="category-list">
            <div 
              v-for="cat in categories" 
              :key="cat.id"
              class="category-item"
              :class="{ active: cat.active }"
              @click="selectCategory(cat.id)"
            >
              <div class="category-checkbox">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span class="category-label">{{ cat.name }}</span>
            </div>
          </div>
        </div>

        <button class="btn-reset" @click="resetFilters">Reset Filters</button>
      </aside>

      <!-- Products Area -->
      <div class="products-area">
        <!-- Market Loading State -->
        <div class="market-loading" v-if="loading">
          <div class="market-loading-bar">
            <div class="market-loading-bar-fill"></div>
          </div>
          <span class="market-loading-text">LOADING ITEMS</span>
        </div>

        <template v-else>
          <div class="products-grid" v-if="paginatedItems.length > 0">
            <div v-for="item in paginatedItems" :key="item.id" class="product-card" @click="openDetail(item)">
              <div class="card-glow"></div>
              <div class="card-border"></div>
              <div class="product-image">
                <div class="image-bg"></div>
                <img v-if="item.image" :src="item.image" :alt="item.name" />
                <div v-else class="product-placeholder">
                  <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                </div>
                <span class="product-category">{{ item.category }}</span>
              </div>
              <div class="product-info">
                <h4 class="product-name">{{ item.name }}</h4>
                <div class="product-footer">
                  <div class="price-tag">
                    <span class="price-value">{{ parseFloat(Number(item.price).toFixed(2)) }}</span>
                    <span class="price-currency">ArkGem</span>
                  </div>
                  <button
                    type="button"
                    class="btn-buy"
                    :class="{ 'in-cart': isInCart(item.id) }"
                    @click.stop="addToCart(item)"
                    :disabled="isInCart(item.id)"
                  >
                    <svg v-if="!isInCart(item.id)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="empty-state" v-if="filteredItems.length === 0">
            <h3 class="empty-title">NO ITEMS AVAILABLE</h3>
            <p class="empty-desc">Items will appear here when the market is configured</p>
          </div>
        </template>
      </div>

      <!-- Pagination (outside products-area, spans full width) -->
      <div class="pagination" v-if="totalPages > 1">
        <button class="page-arrow" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        
        <div class="page-numbers">
          <template v-for="(page, idx) in visiblePages" :key="idx">
            <span v-if="page === '...'" class="page-ellipsis">…</span>
            <button 
              v-else
              class="page-number"
              :class="{ active: page === currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>
        </div>
        
        <button class="page-arrow" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
    </div>

    <!-- Purchase Modal -->
    <Teleport to="body">
      <div v-if="showPurchaseModal && selectedProduct" class="purchase-overlay" @click.self="closePurchase">
        <div class="purchase-modal">
          <button class="purchase-close" @click="closePurchase">×</button>
          
          <h2 class="purchase-title">Confirm Purchase</h2>
          
          <div class="purchase-product">
            <div class="purchase-product-image">
              <img v-if="selectedProduct.image" :src="selectedProduct.image" :alt="selectedProduct.name" />
              <div v-else class="product-placeholder-sm">
                <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
              </div>
            </div>
            <div class="purchase-product-info">
              <h3>{{ selectedProduct.name }}</h3>
              <p class="purchase-price">{{ parseFloat(Number(selectedProduct.price).toFixed(2)) }} ArkGem</p>
            </div>
          </div>

          <div v-if="user" class="purchase-balance">
            <div class="balance-row">
              <span>Your Balance:</span>
              <span class="balance-value">{{ parseFloat(Number(user.balance).toFixed(2)) }} ArkGem</span>
            </div>
            <div class="balance-row">
              <span>After Purchase:</span>
              <span class="balance-after" :class="{ negative: !canAfford }">
                {{ parseFloat((user.balance - selectedProduct.price).toFixed(2)) }} ArkGem
              </span>
            </div>
          </div>

          <div v-else class="login-required">
            <p>Please login with Steam to make a purchase</p>
            <button type="button" class="btn-steam-login" @click="openLoginModal">Login with Steam</button>
          </div>

          <div v-if="purchaseResult" class="purchase-result" :class="purchaseResult.success ? 'success' : 'error'">
            {{ purchaseResult.message }}
          </div>

          <div v-if="user" class="purchase-actions">
            <button 
              class="btn-confirm" 
              @click="confirmPurchase" 
              :disabled="!canAfford || purchasing"
            >
              {{ purchasing ? 'Processing...' : (canAfford ? 'Confirm Purchase' : 'Insufficient Balance') }}
            </button>
            <button class="btn-cancel" @click="closePurchase">Cancel</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Product Detail Modal -->
    <Teleport to="body">
      <div v-if="showDetailModal && detailProduct" class="detail-overlay" @click.self="closeDetail">
        <div class="detail-modal">
          <button class="detail-close" @click="closeDetail">×</button>
          
          <div class="detail-content">
            <div class="detail-image-section">
              <div class="detail-image-wrapper">
                <div class="detail-image-glow"></div>
                <img v-if="detailProduct.image" :src="detailProduct.image" :alt="detailProduct.name" />
                <div v-else class="detail-placeholder">
                  <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                </div>
              </div>
            </div>
            
            <div class="detail-info-section">
              <span class="detail-category">{{ detailProduct.category }}</span>
              <h2 class="detail-name">{{ detailProduct.name }}</h2>
              
              <div class="detail-description">
                <h4>Description</h4>
                <p>{{ detailProduct.description || 'No description available.' }}</p>
              </div>
              
              <div class="detail-footer">
                <div class="detail-price">
                  <span class="detail-price-value">{{ parseFloat(Number(detailProduct.price).toFixed(2)) }}</span>
                  <span class="detail-price-currency">ArkGem</span>
                </div>
                
                <!-- Для товаров с количеством (type='quantity') -->
                <div v-if="detailProduct.type === 'quantity' && detailProduct.arkDeliveryType !== 'creature' && !isInCart(detailProduct.id)" class="detail-quantity-section">
                  <div class="quantity-controls">
                    <button class="qty-btn" @click="detailQuantity > 1 && detailQuantity--">−</button>
                    <input 
                      type="number" 
                      class="qty-input" 
                      v-model.number="detailQuantity" 
                      min="1" 
                      max="999"
                      @focus="$event.target.select()"
                    />
                    <button class="qty-btn" @click="detailQuantity++">+</button>
                  </div>
                  <button class="detail-buy-btn" @click.stop="addToCartWithQuantity(detailProduct)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                    <span>Add {{ detailQuantity }} to Cart</span>
                  </button>
                </div>
                
                <!-- Для обычных товаров (skin) -->
                <button 
                  v-else-if="!isInCart(detailProduct.id)"
                  class="detail-buy-btn"
                  @click.stop="addToCart(detailProduct)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                  <span>Add to Cart</span>
                </button>
                
                <!-- После добавления - две кнопки -->
                <div v-else class="detail-cart-actions">
                  <RouterLink to="/cart" class="detail-btn-cart">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                    <span>Go to Cart</span>
                  </RouterLink>
                  <button class="detail-btn-continue" @click="closeDetail">
                    <span>Continue Shopping</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Steam Required Modal -->
    <Teleport to="body">
      <div v-if="showSteamRequiredModal" class="purchase-overlay" @click.self="showSteamRequiredModal = false">
        <div class="purchase-modal steam-modal">
          <button type="button" class="purchase-close" @click="showSteamRequiredModal = false">×</button>

          <div class="steam-icon-wrap">
            <div class="steam-icon-glow"></div>
            <img src="/download.svg" alt="Steam" class="steam-icon" />
          </div>

          <h2 class="purchase-title">Steam Account Required</h2>
          <p class="steam-modal-desc">
            To purchase items, connect your Steam account so items can be delivered directly to your in-game inventory.
          </p>

          <div class="purchase-actions">
            <a :href="steamLinkUrl" class="btn-confirm steam-link-btn">
              <img src="/download.svg" alt="" class="btn-steam-icon" aria-hidden="true" />
              <span>Connect Steam</span>
            </a>
            <button type="button" class="btn-cancel" @click="showSteamRequiredModal = false">Maybe Later</button>
          </div>
        </div>
      </div>
    </Teleport>
  </main>
</template>

<style scoped>
.main-content {
    padding-top: 100px;
    padding-bottom: 30px;
    min-height: 100vh;
}

.page-header {
    text-align: center;
    margin-bottom: 20px;
    padding-top: 10px;
    animation: fadeInDown 0.8s ease forwards;
}

.page-title {
    font-family: var(--font-display);
    font-size: 4rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 8px;
    margin-bottom: 16px;
    background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.page-title-line {
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-accent));
    margin: 0 auto 20px;
    border-radius: 2px;
}

.page-subtitle {
    color: var(--color-text-muted);
    font-size: 1.1rem;
}

/* Market Layout */
.market-layout {
    max-width: var(--container-max);
    margin: 0 auto;
    padding: 0 25px;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 25px;
}

/* Sidebar */
.market-sidebar {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(var(--color-primary-rgb), 0.15);
    border-radius: var(--radius-lg);
    padding: 20px;
    height: fit-content;
    position: sticky;
    top: 100px;
}

.filter-section {
    margin-bottom: 30px;
}

.filter-section:last-child {
    margin-bottom: 0;
}

.filter-title {
    font-family: var(--font-display);
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--color-primary);
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(var(--color-primary-rgb), 0.2);
    line-height: 1;
}

/* Search */
.search-wrapper {
    position: relative;
}

.search-wrapper svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    stroke: var(--color-text-muted);
    fill: none;
    stroke-width: 2;
}

.search-input {
    width: 100%;
    padding: 14px 20px 14px 48px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    border-radius: var(--radius-md);
    color: var(--color-text);
    font-family: var(--font-primary);
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 20px rgba(var(--color-primary-rgb), 0.2);
}

.search-input::placeholder {
    color: var(--color-text-dim);
}

/* Price Range */
.price-range {
    display: flex;
    align-items: center;
    gap: 8px;
}

.price-input {
    flex: 1;
    min-width: 0;
    width: 80px;
    padding: 10px 8px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    border-radius: var(--radius-md);
    color: var(--color-text);
    font-family: var(--font-primary);
    font-size: 0.85rem;
    text-align: center;
}

.price-input:focus {
    outline: none;
    border-color: var(--color-primary);
}

.price-separator {
    color: var(--color-text-muted);
}

/* Categories */
.category-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    padding-right: 5px;
}

.category-list::-webkit-scrollbar {
    width: 4px;
}

.category-list::-webkit-scrollbar-track {
    background: transparent;
}

.category-list::-webkit-scrollbar-thumb {
    background: rgba(var(--color-primary-rgb), 0.3);
    border-radius: 2px;
}

.category-item {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    transition: all 0.3s ease;
}

.category-item:hover {
    background: rgba(var(--color-primary-rgb), 0.1);
}

.category-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(var(--color-primary-rgb), 0.4);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.category-item.active .category-checkbox {
    background: var(--color-primary);
    border-color: var(--color-primary);
}

.category-checkbox svg {
    width: 14px;
    height: 14px;
    stroke: white;
    fill: none;
    stroke-width: 3;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.category-item.active .category-checkbox svg {
    opacity: 1;
}

.category-label {
    color: var(--color-text-muted);
    font-size: 0.95rem;
    transition: color 0.3s ease;
}

.category-item:hover .category-label,
.category-item.active .category-label {
    color: var(--color-text);
}

/* Reset Button */
.btn-reset {
    width: 100%;
    padding: 14px;
    background: transparent;
    border: 1px solid rgba(var(--color-primary-rgb), 0.3);
    border-radius: var(--radius-md);
    color: var(--color-text-muted);
    font-family: var(--font-primary);
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-reset:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(var(--color-primary-rgb), 0.1);
}

/* Custom Select Dropdown */
.custom-select {
    position: relative;
    width: 100%;
    cursor: pointer;
}

.select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(var(--color-primary-rgb), 0.3);
    border-radius: var(--radius-md);
    color: var(--color-text);
    font-family: var(--font-primary);
    font-size: 0.95rem;
    transition: all 0.3s ease;
}

.select-trigger:hover {
    border-color: var(--color-primary);
    background: rgba(0, 0, 0, 0.5);
}

.select-arrow {
    width: 18px;
    height: 18px;
    color: var(--color-primary);
    transition: transform 0.3s ease;
}

.select-arrow.open {
    transform: rotate(180deg);
}

.select-options {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    right: 0;
    background: #0d1117;
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: var(--radius-md);
    overflow: hidden;
    z-index: 100;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.select-option {
    padding: 14px 18px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    transition: all 0.2s ease;
    border-bottom: 1px solid rgba(0, 212, 255, 0.1);
}

.select-option:last-child {
    border-bottom: none;
}

.select-option:hover {
    background: rgba(0, 212, 255, 0.1);
    color: #fff;
}

.select-option.active {
    background: rgba(0, 212, 255, 0.15);
    color: var(--color-primary);
}

/* Market Loading */
.market-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 40px;
    gap: 24px;
}

.market-loading-bar {
    width: 100px;
    height: 3px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 3px;
    overflow: hidden;
}

.market-loading-bar-fill {
    height: 100%;
    width: 40%;
    background: linear-gradient(90deg, transparent, #00d4ff, transparent);
    border-radius: 3px;
    animation: market-slide 1.2s ease-in-out infinite;
}

@keyframes market-slide {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
}

.market-loading-text {
    font-family: var(--font-display);
    font-size: 0.75rem;
    letter-spacing: 4px;
    color: rgba(255, 255, 255, 0.35);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 80px 40px;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(var(--color-primary-rgb), 0.15);
    border-radius: var(--radius-lg);
}

.empty-icon {
    width: 100px;
    height: 100px;
    margin: 0 auto 30px;
    background: rgba(var(--color-primary-rgb), 0.1);
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-icon svg {
    width: 50px;
    height: 50px;
    stroke: var(--color-text-dim);
    fill: none;
    stroke-width: 1.5;
}

.empty-title {
    font-family: var(--font-display);
    font-size: 1.5rem;
    color: var(--color-text-muted);
    margin-bottom: 10px;
}

.empty-desc {
    color: var(--color-text-dim);
    font-size: 0.95rem;
}

.products-area {
    min-width: 0;
    overflow-x: clip;
    width: 100%;
}

/* Products Grid */
.products-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    padding-top: 5px;
}

.product-card {
    position: relative;
    background: linear-gradient(165deg, rgba(15, 20, 30, 0.95) 0%, rgba(5, 10, 20, 0.98) 100%);
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 0;
    max-width: 100%;
}

.card-glow {
    position: absolute;
    inset: 0;
    opacity: 0;
    background: radial-gradient(circle at 50% 0%, rgba(0, 212, 255, 0.15) 0%, transparent 60%);
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 1;
}

.card-border {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 1px;
    background: linear-gradient(165deg, rgba(0, 212, 255, 0.3) 0%, rgba(0, 212, 255, 0.05) 50%, transparent 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 2;
}

.product-card:hover {
    transform: translateY(-5px);
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.4),
        0 0 60px rgba(0, 212, 255, 0.1);
}

.product-card:hover .card-glow {
    opacity: 1;
}

.product-card:hover .card-border {
    background: linear-gradient(165deg, rgba(0, 212, 255, 0.6) 0%, rgba(0, 212, 255, 0.2) 50%, rgba(0, 212, 255, 0.1) 100%);
}

.product-image {
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    position: relative;
    overflow: hidden;
    width: 100%;
}

.image-bg {
    position: absolute;
    inset: 0;
    background: 
        radial-gradient(ellipse at 50% 80%, rgba(0, 212, 255, 0.08) 0%, transparent 50%),
        linear-gradient(180deg, rgba(0, 20, 40, 0.5) 0%, transparent 100%);
    z-index: 0;
}

.product-image img {
    position: relative;
    max-width: 80%;
    max-height: 120px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1;
}

.product-card:hover .product-image img {
    transform: scale(1.08) translateY(-5px);
    filter: drop-shadow(0 15px 40px rgba(0, 212, 255, 0.2));
}

.product-category {
    position: absolute;
    top: 10px;
    left: 10px;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: #fff;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.9) 0%, rgba(0, 160, 200, 0.9) 100%);
    padding: 4px 10px;
    border-radius: 20px;
    font-weight: 600;
    z-index: 3;
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.product-placeholder {
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 212, 255, 0.05);
    border-radius: 50%;
    z-index: 1;
}

.product-placeholder svg {
    width: 50px;
    height: 50px;
    stroke: rgba(0, 212, 255, 0.3);
    fill: none;
    stroke-width: 1;
}

.product-info {
    padding: 14px 16px;
    position: relative;
    z-index: 2;
}

.product-name {
    font-family: var(--font-display);
    font-size: 1.1rem;
    color: #fff;
    margin-bottom: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

.product-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
}

.price-tag {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.price-value {
    font-family: var(--font-display);
    font-size: 1.25rem;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
}

.price-currency {
    font-size: 0.8rem;
    color: var(--color-primary);
    font-weight: 600;
    letter-spacing: 1px;
}

.btn-buy {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    color: #000;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 212, 255, 0.3);
}

.btn-buy svg {
    width: 20px;
    height: 20px;
}

.btn-buy:hover:not(:disabled) {
    transform: scale(1.1);
    box-shadow: 0 6px 30px rgba(0, 212, 255, 0.5);
}

.btn-buy.in-cart {
    background: linear-gradient(135deg, #00ff64 0%, #00cc50 100%);
    cursor: default;
    box-shadow: 0 4px 20px rgba(0, 255, 100, 0.3);
}

.btn-buy:disabled {
    cursor: default;
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Pagination */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    margin-top: 40px;
    padding: 20px 0;
    grid-column: 1 / -1;
}

.page-arrow {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #888;
    cursor: pointer;
    transition: all 0.3s ease;
}

.page-arrow svg {
    width: 20px;
    height: 20px;
}

.page-arrow:hover:not(:disabled) {
    background: rgba(0, 212, 255, 0.1);
    border-color: var(--color-primary);
    color: var(--color-primary);
}

.page-arrow:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    align-items: center;
    gap: 8px;
}

.page-number {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: #888;
    font-family: var(--font-display);
    font-size: 1.1rem;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.page-number:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.05);
}

.page-number.active {
    color: var(--color-primary);
    background: rgba(0, 212, 255, 0.15);
    font-weight: 700;
    text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
}

.page-ellipsis {
    color: #555;
    font-size: 0.9rem;
    padding: 0 4px;
    user-select: none;
}

/* Mobile filter toggle - hidden on desktop */
.mobile-filter-toggle {
    display: none;
    grid-column: 1 / -1;
}

/* Responsive */
@media (max-width: 1200px) {
    .market-layout {
        grid-template-columns: 1fr;
    }
    
    .market-sidebar {
        position: static;
    }
    
    .products-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .main-content {
        padding-top: 80px;
        min-height: 100vh;
    }
    
    .page-header {
        padding: 20px 0 20px;
    }
    
    .page-title {
        font-size: 2rem;
        letter-spacing: 4px;
    }
    
    .page-subtitle {
        font-size: 0.9rem;
        opacity: 0.8;
    }
    
    .market-layout {
        padding: 0 12px;
        gap: 12px;
        display: flex;
        flex-direction: column;
    }
    
    /* Mobile filter toggle */
    .mobile-filter-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        padding: 12px;
        background: rgba(0, 212, 255, 0.08);
        border: 1px solid rgba(0, 212, 255, 0.2);
        border-radius: 12px;
        color: var(--color-primary);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
        cursor: pointer;
        transition: all 0.3s ease;
    }
    
    .mobile-filter-toggle svg {
        width: 16px;
        height: 16px;
    }
    
    .mobile-filter-toggle:hover {
        background: rgba(0, 212, 255, 0.15);
    }
    
    /* Sidebar hidden by default on mobile */
    .market-sidebar {
        display: none;
        position: static;
        padding: 16px;
        border-radius: 12px;
    }
    
    .market-sidebar.sidebar-open {
        display: block;
    }
    
    .filter-section {
        margin-bottom: 16px;
    }
    
    .filter-title {
        font-size: 0.7rem;
        letter-spacing: 2px;
        margin-bottom: 10px;
        padding-bottom: 8px;
    }
    
    .search-input {
        padding: 10px 12px 10px 38px;
        font-size: 0.85rem;
        border-radius: 10px;
    }
    
    .select-trigger {
        padding: 10px 14px;
        border-radius: 10px;
        font-size: 0.85rem;
    }
    
    .select-option {
        padding: 10px 14px;
        font-size: 0.85rem;
    }
    
    .price-input {
        padding: 8px 6px;
        font-size: 0.8rem;
        border-radius: 8px;
    }
    
    .category-list {
        max-height: 180px;
        overflow-y: auto;
    }
    
    .category-item {
        padding: 8px 10px;
        border-radius: 8px;
    }
    
    .category-checkbox {
        width: 18px;
        height: 18px;
    }
    
    .category-label {
        font-size: 0.8rem;
    }
    
    .btn-reset {
        padding: 10px;
        font-size: 0.7rem;
        border-radius: 10px;
    }
    
    /* Product Cards — 2 columns on mobile */
    .products-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
    }
    
    .product-card {
        border-radius: 12px;
    }
    
    .product-card::before {
        display: none;
    }
    
    .product-card:hover {
        transform: none;
    }
    
    .product-image {
        height: 100px;
        padding: 10px;
    }
    
    .product-image img {
        max-height: 80px;
        max-width: 90%;
    }
    
    .product-category {
        top: 6px;
        left: 6px;
        font-size: 0.5rem;
        padding: 3px 6px;
        border-radius: 4px;
    }
    
    .product-info {
        padding: 8px 10px 10px;
        text-align: left;
    }
    
    .product-name {
        font-size: 0.75rem;
        margin-bottom: 6px;
        letter-spacing: 0.5px;
    }
    
    .product-footer {
        justify-content: space-between;
        gap: 8px;
    }
    
    .price-value {
        font-size: 0.9rem;
    }
    
    .price-currency {
        font-size: 0.6rem;
    }
    
    .btn-buy {
        width: 32px;
        height: 32px;
        border-radius: 8px;
    }
    
    .btn-buy svg {
        width: 14px;
        height: 14px;
    }
    
    .pagination {
        gap: 6px;
        margin-top: 20px;
    }
    
    .page-arrow {
        width: 34px;
        height: 34px;
    }
    
    .page-number {
        width: 30px;
        height: 30px;
        font-size: 0.8rem;
    }
}

@media (max-width: 480px) {
    .market-layout {
        padding: 0 8px;
        gap: 10px;
    }
    
    .page-title {
        font-size: 1.8rem;
    }
    
    .page-subtitle {
        font-size: 0.8rem;
    }
    
    .products-grid {
        gap: 8px;
    }
    
    .product-image {
        height: 85px;
        padding: 8px;
    }
    
    .product-image img {
        max-height: 65px;
    }
    
    .product-category {
        font-size: 0.45rem;
        padding: 2px 5px;
    }
    
    .product-info {
        padding: 6px 8px 8px;
    }
    
    .product-name {
        font-size: 0.65rem;
        margin-bottom: 4px;
    }
    
    .price-value {
        font-size: 0.8rem;
    }
    
    .price-currency {
        font-size: 0.55rem;
    }
    
    .btn-buy {
        width: 28px;
        height: 28px;
        border-radius: 6px;
    }
    
    .btn-buy svg {
        width: 12px;
        height: 12px;
    }
    
    .page-number {
        width: 28px;
        height: 28px;
        font-size: 0.75rem;
    }
    
    .page-arrow {
        width: 30px;
        height: 30px;
    }
}

/* Purchase Modal */
.purchase-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(5px);
}

.purchase-modal {
    background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
    border: 2px solid #333;
    border-radius: 16px;
    padding: 2rem;
    width: 100%;
    max-width: 450px;
    position: relative;
}

.purchase-close {
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    color: #666;
    font-size: 28px;
    cursor: pointer;
    line-height: 1;
    transition: color 0.2s;
}

.purchase-close:hover {
    color: var(--color-primary);
}

.purchase-title {
    font-family: var(--font-display);
    font-size: 1.5rem;
    color: var(--color-primary);
    text-align: center;
    margin-bottom: 1.5rem;
}

.purchase-product {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    margin-bottom: 1.5rem;
}

.purchase-product-image {
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.5);
    flex-shrink: 0;
}

.purchase-product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.product-placeholder-sm {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.product-placeholder-sm svg {
    width: 40px;
    height: 40px;
    stroke: #555;
    fill: none;
    stroke-width: 1;
}

.purchase-product-info h3 {
    color: #fff;
    margin-bottom: 0.5rem;
}

.purchase-price {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-accent);
}

.purchase-balance {
    margin-bottom: 1.5rem;
}

.balance-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid #333;
}

.balance-row span:first-child {
    color: #888;
}

.balance-value {
    color: #fff;
    font-weight: 600;
}

.balance-after {
    color: #2ecc71;
    font-weight: 600;
}

.balance-after.negative {
    color: #e74c3c;
}

.login-required {
    text-align: center;
    padding: 1.5rem;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    margin-bottom: 1rem;
}

.login-required p {
    color: #888;
    margin-bottom: 1rem;
}

.btn-steam-login {
    display: inline-block;
    padding: 10px 24px;
    background: #171a21;
    color: #fff;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 600;
}

.purchase-result {
    text-align: center;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-weight: 600;
}

.purchase-result.success {
    background: rgba(46, 204, 113, 0.2);
    color: #2ecc71;
}

.purchase-result.error {
    background: rgba(231, 76, 60, 0.2);
    color: #e74c3c;
}

.purchase-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.btn-confirm {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    border: none;
    border-radius: 8px;
    color: #000;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-confirm:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 212, 255, 0.3);
}

.btn-confirm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #555;
    color: #888;
}

.btn-cancel {
    width: 100%;
    padding: 12px;
    background: transparent;
    border: 1px solid #555;
    border-radius: 8px;
    color: #888;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel:hover {
    border-color: #888;
    color: #fff;
}

/* Product Detail Modal */
.detail-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.detail-modal {
    background: linear-gradient(165deg, rgba(15, 20, 35, 0.98) 0%, rgba(5, 10, 25, 0.99) 100%);
    border: 1px solid rgba(0, 212, 255, 0.2);
    border-radius: 24px;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 
        0 0 80px rgba(0, 212, 255, 0.1),
        0 30px 60px rgba(0, 0, 0, 0.5);
    animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.detail-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #888;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
}

.detail-close:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: var(--color-primary);
    color: var(--color-primary);
    transform: rotate(90deg);
}

.detail-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 450px;
}

.detail-image-section {
    padding: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(0, 20, 40, 0.5) 0%, rgba(0, 10, 30, 0.8) 100%);
    border-radius: 24px 0 0 24px;
    position: relative;
    overflow: hidden;
}

.detail-image-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.08) 0%, transparent 60%);
    pointer-events: none;
}

.detail-image-wrapper {
    position: relative;
    max-width: 100%;
    max-height: 350px;
}

.detail-image-glow {
    position: absolute;
    inset: -30px;
    background: radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%);
    filter: blur(20px);
    z-index: 0;
}

.detail-image-wrapper img {
    position: relative;
    max-width: 100%;
    max-height: 350px;
    object-fit: contain;
    filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.5));
    z-index: 1;
}

.detail-placeholder {
    width: 150px;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 212, 255, 0.05);
    border-radius: 50%;
}

.detail-placeholder svg {
    width: 80px;
    height: 80px;
    stroke: rgba(0, 212, 255, 0.3);
    fill: none;
    stroke-width: 1;
}

.detail-info-section {
    padding: 50px 40px;
    display: flex;
    flex-direction: column;
}

.detail-category {
    display: inline-block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #fff;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.9) 0%, rgba(0, 160, 200, 0.9) 100%);
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
    margin-bottom: 20px;
    width: fit-content;
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.detail-name {
    font-family: var(--font-display);
    font-size: 2rem;
    color: #fff;
    margin-bottom: 20px;
    letter-spacing: 1px;
    line-height: 1.2;
}

.detail-description {
    flex: 1;
    margin-bottom: 30px;
}

.detail-description h4 {
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--color-primary);
    margin-bottom: 12px;
}

.detail-description p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.8;
}

.detail-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding-top: 25px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-price {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.detail-price-value {
    font-family: var(--font-display);
    font-size: 2.5rem;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 0 30px rgba(0, 212, 255, 0.4);
}

.detail-price-currency {
    font-size: 1rem;
    color: var(--color-primary);
    font-weight: 600;
    letter-spacing: 2px;
}

.detail-buy-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, var(--color-primary) 0%, #0095b3 100%);
    color: #000;
    border: none;
    padding: 16px 32px;
    border-radius: 30px;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 25px rgba(0, 212, 255, 0.4);
}

.detail-buy-btn svg {
    width: 20px;
    height: 20px;
}

.detail-buy-btn:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 40px rgba(0, 212, 255, 0.5);
}

.detail-buy-btn.in-cart {
    background: linear-gradient(135deg, #00ff64 0%, #00cc50 100%);
    cursor: default;
    box-shadow: 0 6px 25px rgba(0, 255, 100, 0.4);
}

.detail-buy-btn:disabled {
    cursor: default;
}

/* Кнопки после добавления в корзину */
.detail-cart-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}

.detail-btn-cart {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px 28px;
    background: linear-gradient(135deg, #00ff64 0%, #00cc50 100%);
    border: none;
    border-radius: var(--radius-sm);
    color: #000;
    font-weight: 700;
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 25px rgba(0, 255, 100, 0.4);
}

.detail-btn-cart svg {
    width: 18px;
    height: 18px;
    stroke: #000;
}

.detail-btn-cart:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 35px rgba(0, 255, 100, 0.5);
}

.detail-btn-continue {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.detail-btn-continue:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(var(--color-primary-rgb), 0.1);
}

/* Quantity controls */
.detail-quantity-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

.quantity-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    background: rgba(0, 20, 40, 0.6);
    border: 1px solid rgba(var(--color-primary-rgb), 0.2);
    border-radius: var(--radius-sm);
    padding: 10px 20px;
}

.qty-btn {
    width: 36px;
    height: 36px;
    border: 1px solid rgba(var(--color-primary-rgb), 0.3);
    background: rgba(var(--color-primary-rgb), 0.1);
    color: var(--color-primary);
    font-size: 1.3rem;
    font-weight: 700;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.qty-btn:hover {
    background: var(--color-primary);
    color: #000;
    transform: scale(1.1);
}

.qty-input {
    font-size: 1.5rem;
    font-weight: 700;
    width: 55px;
    text-align: center;
    color: var(--color-text);
    background: transparent;
    border: none;
    outline: none;
    font-family: var(--font-display);
    -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.qty-input:focus {
    background: rgba(var(--color-primary-rgb), 0.08);
    border-radius: 6px;
}

@media (max-width: 768px) {
    .detail-content {
        grid-template-columns: 1fr;
    }
    
    .detail-image-section {
        border-radius: 24px 24px 0 0;
        padding: 30px;
        min-height: 250px;
    }
    
    .detail-info-section {
        padding: 30px;
    }
    
    .detail-name {
        font-size: 1.5rem;
    }
    
    .detail-footer {
        flex-direction: column;
        align-items: stretch;
    }
    
    .detail-price {
        justify-content: center;
        margin-bottom: 10px;
    }
    
    .detail-buy-btn {
        justify-content: center;
    }
    
    .purchase-modal {
        padding: 25px;
        max-width: 95%;
    }
    
    .purchase-title {
        font-size: 1.3rem;
    }
}

@media (max-width: 480px) {
    .detail-modal {
        max-width: 95%;
        margin: 15px;
    }
    
    .detail-close {
        width: 36px;
        height: 36px;
        top: 15px;
        right: 15px;
        font-size: 1.3rem;
    }
    
    .detail-image-section {
        padding: 20px;
        min-height: 200px;
    }
    
    .detail-image-wrapper img {
        max-height: 180px;
    }
    
    .detail-placeholder {
        width: 100px;
        height: 100px;
    }
    
    .detail-placeholder svg {
        width: 50px;
        height: 50px;
    }
    
    .detail-info-section {
        padding: 20px;
    }
    
    .detail-category {
        font-size: 0.7rem;
    }
    
    .detail-name {
        font-size: 1.2rem;
        margin-bottom: 15px;
    }
    
    .detail-description h4 {
        font-size: 0.85rem;
    }
    
    .detail-description p {
        font-size: 0.85rem;
    }
    
    .detail-price-value {
        font-size: 2rem;
    }
    
    .detail-buy-btn {
        padding: 12px 20px;
        font-size: 0.9rem;
    }
    
    .purchase-modal {
        padding: 20px;
    }
    
    .purchase-title {
        font-size: 1.1rem;
    }
    
    .purchase-info {
        padding: 15px;
    }
    
    .purchase-product-name {
        font-size: 1rem;
    }
    
    .btn-confirm,
    .btn-cancel {
        padding: 12px;
        font-size: 0.9rem;
    }
}

/* Steam Required Modal — extends .purchase-modal */
.steam-modal {
  max-width: 420px;
  text-align: center;
}

.steam-icon-wrap {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0.25rem auto 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.steam-icon-glow {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(var(--color-primary-rgb), 0.35), transparent 70%);
  filter: blur(14px);
  pointer-events: none;
  animation: steam-pulse 2.4s ease-in-out infinite;
}

@keyframes steam-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}

.steam-icon {
  position: relative;
  width: 56px;
  height: 56px;
  filter: drop-shadow(0 0 10px rgba(var(--color-primary-rgb), 0.5));
}

.steam-modal-desc {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 1.5rem;
}

.steam-link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
}

.btn-steam-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0);
}
</style>
